import { useRef } from "react";
import { motion, useScroll, useTransform, useReducedMotion } from "framer-motion";
import { ArrowDown, ArrowUpRight } from "lucide-react";
import { HERO } from "@/constants/testIds";
import { PROFILE } from "@/data/content";
import { MaskLines } from "@/lib/motion";
import { HeroCurve } from "./EpiCurve";

const FADE_UP_HIDDEN = { opacity: 0, y: 16 };
const FADE_SHOW_Y = { opacity: 1, y: 0 };
const EYEBROW_T = { duration: 0.8, delay: 0.3 };
const TAGLINE_HIDDEN = { opacity: 0 };
const TAGLINE_SHOW = { opacity: 1 };
const TAGLINE_T = { duration: 1, delay: 1 };
const BLOCK_HIDDEN = { opacity: 0, y: 20 };
const META_T = { duration: 0.9, delay: 1.15 };
const CTA_T = { duration: 0.9, delay: 1.3 };
const PORTRAIT_HIDDEN = { opacity: 0, scale: 1.05 };
const PORTRAIT_SHOW = { opacity: 1, scale: 1 };
const PORTRAIT_T = { duration: 1.3, ease: [0.22, 1, 0.36, 1], delay: 0.5 };
const SCROLL_HINT_ANIMATE = { y: [0, 8, 0] };
const SCROLL_HINT_T = { duration: 2, repeat: Infinity, ease: "easeInOut" };
const FIRST_LINE = [PROFILE.first];
const LAST_LINE = [PROFILE.last];

export const Hero = () => {
  const ref = useRef(null);
  const reduce = useReducedMotion();
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start start", "end start"],
  });
  const textY = useTransform(scrollYProgress, [0, 1], [0, reduce ? 0 : -60]);

  return (
    <section
      ref={ref}
      data-testid={HERO.root}
      id="hero"
      className="relative min-h-screen pt-28 pb-16 md:pt-32 flex items-end overflow-hidden"
    >
      {/* faint background epi-curve */}
      <HeroCurve className="absolute left-0 right-0 bottom-[18%] w-full h-[220px] opacity-40 z-0" />

      <div className="relative z-10 w-full max-w-screen-2xl mx-auto px-6 md:px-12 lg:px-20">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-8 items-end">
          {/* Text block */}
          <motion.div style={{ y: textY }} className="lg:col-span-8 order-2 lg:order-1">
            <motion.div
              initial={reduce ? false : FADE_UP_HIDDEN}
              animate={FADE_SHOW_Y}
              transition={EYEBROW_T}
            >
              <span className="eyebrow">Epidemiology & Biostatistics</span>
            </motion.div>

            <h1 className="font-serif-display font-light text-[#16324f] mt-6 mb-7 text-[clamp(3.4rem,11vw,10rem)] leading-[0.86] tracking-[-0.03em]">
              <MaskLines lines={FIRST_LINE} delay={0.4} />
              <MaskLines
                lines={LAST_LINE}
                delay={0.55}
                className="font-normal text-[#b5423a]"
              />
            </h1>

            <motion.p
              initial={reduce ? false : TAGLINE_HIDDEN}
              animate={TAGLINE_SHOW}
              transition={TAGLINE_T}
              className="max-w-[30rem] text-[17px] md:text-lg text-[#48606f] leading-relaxed"
            >
              {PROFILE.tagline}
            </motion.p>

            <motion.div
              initial={reduce ? false : BLOCK_HIDDEN}
              animate={FADE_SHOW_Y}
              transition={META_T}
              className="mt-8 flex flex-wrap items-center gap-x-6 gap-y-2 font-mono-plex text-[12.5px] text-[#48606f]"
            >
              <span>{PROFILE.location}</span>
              <span className="w-1 h-1 rounded-full bg-[#b5423a]" />
              <span>{PROFILE.email}</span>
              <span className="w-1 h-1 rounded-full bg-[#b5423a]" />
              <span>{PROFILE.phone}</span>
            </motion.div>

            <motion.div
              initial={reduce ? false : BLOCK_HIDDEN}
              animate={FADE_SHOW_Y}
              transition={CTA_T}
              className="mt-9 flex flex-wrap gap-4"
            >
              <a
                data-testid={HERO.emailCta}
                href={`mailto:${PROFILE.email}`}
                className="group flex items-center gap-2 bg-[#16324f] text-[#f5f6f2] font-mono-plex text-[12.5px] uppercase tracking-[0.12em] px-6 py-3.5 rounded-full hover:bg-[#b5423a] transition-colors duration-400"
              >
                Email me
                <ArrowUpRight size={15} className="group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform duration-300" />
              </a>
              <button
                data-testid={HERO.viewWork}
                onClick={() => document.getElementById("experience")?.scrollIntoView({ behavior: "smooth" })}
                className="flex items-center gap-2 border border-[#16324f] text-[#16324f] font-mono-plex text-[12.5px] uppercase tracking-[0.12em] px-6 py-3.5 rounded-full hover:bg-[#16324f] hover:text-[#f5f6f2] transition-colors duration-400"
              >
                View experience
              </button>
            </motion.div>
          </motion.div>

          {/* Portrait */}
          <motion.div
            initial={reduce ? false : PORTRAIT_HIDDEN}
            animate={PORTRAIT_SHOW}
            transition={PORTRAIT_T}
            className="lg:col-span-4 order-1 lg:order-2 justify-self-start lg:justify-self-end w-full"
          >
            <div
              data-testid={HERO.portrait}
              className="relative w-[62%] sm:w-[46%] lg:w-full max-w-[320px] mx-auto lg:mx-0"
            >
              <div className="relative overflow-hidden rounded-t-[999px] rounded-b-[8px] aspect-[3/4] bg-[#dfe1d5] shadow-[0_30px_80px_-30px_rgba(22,50,79,0.5)]">
                <img
                  src={PROFILE.portrait}
                  alt="Portrait of Catherine Magawa"
                  className="w-full h-full object-cover object-center contrast-[1.02]"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-[#16324f]/25 via-transparent to-transparent" />
              </div>
              <div className="absolute -top-3 -left-3 -right-3 -bottom-3 border border-dashed border-[#b5423a]/50 rounded-t-[999px] rounded-b-[10px] pointer-events-none" />
              <span className="absolute -left-2 top-6 font-mono-plex text-[10px] tracking-[0.2em] text-[#2e7d6b] rotate-180 [writing-mode:vertical-rl]">
                CLASS OF 2026 · NAIROBI
              </span>
            </div>
          </motion.div>
        </div>

        <motion.div
          animate={SCROLL_HINT_ANIMATE}
          transition={SCROLL_HINT_T}
          className="hidden md:flex items-center gap-3 mt-16 font-mono-plex text-[11px] uppercase tracking-[0.2em] text-[#48606f]"
        >
          <ArrowDown size={14} className="text-[#b5423a]" />
          Scroll to explore
        </motion.div>
      </div>
    </section>
  );
};
